import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUsers,
  addUser,
  updateUser,
  deleteUser,
  bulkActionUsers,
  resetUserPassword,
  toggleUserTwoFactor
} from '../../../redux/UserManagement/actions';
import type { PlatformUser, UserFilters, UserBulkAction } from '../types/user';

// Selector types
interface RootState {
  userManagement: {
    users: PlatformUser[];
    selectedUsers: string[];
    filters: UserFilters;
    loading: boolean;
    error: string | null;
    addUserLoading: boolean;
    updateUserLoading: boolean;
    deleteUserLoading: boolean;
    bulkActionLoading: boolean;
    resetPasswordLoading: boolean;
    toggleTwoFactorLoading: boolean;
  };
  auth: {
    user: PlatformUser | null;
    isAuthenticated: boolean;
  };
}

export const useUserManagementRedux = () => {
  const dispatch = useDispatch();
  
  // Selectors - get state from Redux store
  const {
    users,
    selectedUsers,
    filters,
    loading,
    error,
    addUserLoading,
    updateUserLoading,
    deleteUserLoading,
    bulkActionLoading,
    resetPasswordLoading,
    toggleTwoFactorLoading
  } = useSelector((state: RootState) => state.userManagement);
  
  const currentUser = useSelector((state: RootState) => state.auth.user);



  const filterUsers = useCallback((users: PlatformUser[], filters: UserFilters) => {
    return users.filter(user => {
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesSearch = 
          user.name.toLowerCase().includes(searchTerm) ||
          user.email.toLowerCase().includes(searchTerm);
        if (!matchesSearch) return false;
      }

      if (filters.roles?.length && !filters.roles.includes(user.role)) {
        return false;
      }

      if (filters.status?.length && !filters.status.includes(user.status)) {
        return false;
      }

      if (filters.groups?.length && !filters.groups.some(g => user.groups?.includes(g))) {
        return false;
      }

      if (filters.department?.length && !filters.department.includes(user.profile.department?.name || '')) {
        return false;
      }

      if (filters.company?.length && !filters.company.includes(user.profile.company?.name || '')) {
        return false;
      }

      return true;
    });
  }, []);

  const getFilteredUsers = useCallback(() => {
    return filterUsers(users, filters);
  }, [users, filters, filterUsers]);


  // Actions
  const loadUsers = useCallback((filters: UserFilters = {}) => {
    dispatch(fetchUsers(filters));
  }, [dispatch]);

  const addUserAction = useCallback((userData: Omit<PlatformUser, 'id' | 'createdAt' | 'lastLogin' | 'requirePasswordChange'>, currentUser: PlatformUser) => {
    dispatch(addUser(userData, currentUser));
  }, [dispatch]);

  const updateUserAction = useCallback((userData: Partial<PlatformUser> & { id: string }, currentUser: PlatformUser) => {
    dispatch(updateUser(userData, currentUser));
  }, [dispatch]);

  const deleteUserAction = useCallback((userId: string, currentUser: PlatformUser) => {
    dispatch(deleteUser(userId, currentUser));
  }, [dispatch]);

  const bulkAction = useCallback((bulkAction: UserBulkAction, currentUser: PlatformUser) => {
    dispatch(bulkActionUsers(bulkAction, currentUser));
  }, [dispatch]);

  const resetPasswordAction = useCallback((email: string, ttlSeconds: number, platform : string) => {
    console.log("Dispatching resetUserPassword with:", email, ttlSeconds, platform);
    dispatch(resetUserPassword(email, ttlSeconds, platform));
  }, [dispatch]);

  const toggleTwoFactorAction = useCallback((userId: string, currentUser: PlatformUser) => {
    dispatch(toggleUserTwoFactor(userId, currentUser));
  }, [dispatch]);

  // Load users on mount
  useEffect(() => {
    if(users.length === 0 && !loading){
      loadUsers();
    }
  }, [loadUsers]);


  

  // Test function to verify Redux is working
  const testReduxConnection = useCallback(() => {
  }, [users, loading, error]);

  return {
    // State from Redux store
    users,
    selectedUsers,
    filters,
    loading,
    error,
    addUserLoading,
    updateUserLoading,
    deleteUserLoading,
    bulkActionLoading,
    resetPasswordLoading,
    toggleTwoFactorLoading,
    currentUser,
    getFilteredUsers,
    // Actions
    loadUsers,
    addUser: addUserAction,
    updateUser: updateUserAction,
    deleteUser: deleteUserAction,
    bulkAction,
    resetPassword: resetPasswordAction,
    toggleTwoFactor: toggleTwoFactorAction,
    
    // Test function
    testReduxConnection
  };
}; 